import React, { useState, useRef } from "react";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { updateFormField } from "../../../redux/slices/vendorFormSlice";
import { Modal } from "@mui/material";

export default function FileUpload() {
  const [isImage, setIsImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { formData } = useSelector((state) => state.vendorForm);

  const allowedExtensions = [
    // "pdf",
    // "xls",
    // "xlsx",
    // "doc",
    // "docx",
    "png",
    "jpg",
    "jpeg",
  ];

  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return;

    const extension = selectedFile.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      alert("Invalid file type");
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      alert("File size should not exceed 2MB");
      return;
    }

    const formDataNew = new FormData();
    formDataNew.append("files", selectedFile);

    setUploadLoading(true);
    try {
      const token = localStorage.getItem("vendorToken");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/upload`,
        formDataNew,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.success) {
        const uploadedFile = response.data.data[0]; // Should be URL or object with .url
        dispatch(
          updateFormField({ name: "chequeBookImageURL", value: uploadedFile })
        );
        setFile(selectedFile);
      }
    } catch (err) {
      console.error("Error uploading file", err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileUpload(selectedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileUpload(droppedFile);
  };

  const getFileURL = () => {
    // Adapt to how your API responds (either string or object with .url)
    if (!formData.chequeBookImageURL) return null;
    return typeof formData.chequeBookImageURL === "string"
      ? formData.chequeBookImageURL
      : formData.chequeBookImageURL.url;
  };

  const fileURL = getFileURL();

  return (
    <div className="max-w-md w-full">
      <label className="block text-gray-700 text-md font-medium mb-2">
        Cheque Book
        {/* <span className="text-red-500">*</span> */}
      </label>
      <div
        className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <UploadFileOutlinedIcon className="w-8 h-8 text-blue-500" />
        </div>

        <div className="text-center">
          <span className="text-blue-500 text-xl font-medium">Click here</span>
          <span className="text-gray-600 text-xl">
            {" "}
            to upload or drag file.
          </span>
        </div>

        <p className="text-gray-400 mt-2">
          Supported Format: JPEG, JPG, PNG (max: 2MB)
        </p>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept=".jpg,.jpeg,.png"
        />

        {uploadLoading && <p className="text-blue-500 mt-4">Uploading...</p>}

        {file && (
          <div className="mt-4 text-sm text-gray-600 flex flex-col items-center">
            <div>Selected file: {file.name}</div>
            {fileURL && (
              <button
                className="ml-4 text-blue-500 underline"
                onClick={(e) => {
                  e.stopPropagation(); // ✅ Prevents file input from opening
                  setSelectedImage(formData.chequeBookImageURL);
                  setIsImage(true);
                }}
              >
                Preview
              </button>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Modal
        open={isImage}
        onClose={() => {
          setIsImage(false);
          setSelectedImage(null);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="h-fit flex justify-center items-center mx-auto relative bg-white p-4 rounded-md"
          onClick={(e) => e.stopPropagation()}
        >
          {selectedImage?.endsWith(".pdf") ? (
            <iframe
              src={selectedImage}
              title="PDF Preview"
              className="w-[80vw] h-[90vh] rounded"
            />
          ) : (
            <img
              src={selectedImage}
              alt="Uploaded"
              className="object-contain max-w-[80vw] max-h-[90vh] rounded"
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
